import { delay, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmIndexPage } from 'APIPackage/KnmModule/interfaces/IKnmIndexPage';
import { exportKnmList } from 'APIPackage/KnmListModule/services/knmListApi';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import {
  KNM_NOT_AVAILABLE_UNLINK_FROM_PLAN,
  KNM_UNLIST_ERROR,
  KNM_UNLIST_SUCCESSFULLY,
} from 'CorePackage/MainModule/constants/notices';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { exportSimilaritiesInKnmList } from 'APIPackage/SimilarityModule/services/similaritiesApi';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { KnmRegistryTypeEnum } from 'APIPackage/KnmModule/enums/KnmRegistryTypeEnum';
import { exportRegistryByType } from 'APIPackage/KnmModule/services/knmRegistryApi';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { knmListFilterSetPlanNum } from 'KnmListPackage/KnmListFiltersModule/actions/knmListFilterActions';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { IKnmListFilterParams } from 'APIPackage/KnmListModule/interfaces/IKnmListFilterParams';
import { knmDeleteSagaFunc } from 'KnmPackage/MainModule/sagas/knmDeleteSagaFunc';
import { REFRESH_DELAY } from 'CorePackage/MainModule/constants/refreshDelay';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import {
  fetchKnmRestoreById,
  fetchKnmsCancel,
  fetchKnmsDisapprove,
  fetchKnmsUpdateStartDate,
  fetchKnmsUpdateDuration,
} from 'APIPackage/KnmModule/services/knmActionsApi';
import { fetchGetKnmUnresolvedViolations, fetchKnm } from 'APIPackage/KnmModule/services/knmApi';
import { fetchKnmFindIndex } from 'APIPackage/KnmModule/services/knmListApi';
import { fetchErknmRemoveFromPlan, fetchKnmRemoveFromPlan } from 'APIPackage/KnmModule/services/knmPlanApi';
import { fetchPlanById } from 'APIPackage/PlanListModule/services/knmPlanApi';
import { getPlanErknmStatusByStatusTitle } from 'APIPackage/PlanListModule/common/getPlanErknmStatusByStatusTitle';
import { getIsPlanErknmStatusApproved } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { PlanStatusEnum } from 'APIPackage/PlanListModule/enums/PlanStatusEnum';
import { getErpPlanLabel } from 'PlanListPackage/MainModule/common/mapPlanStatus';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import {
  hasKnmUnresolvedViolations,
  knmListCancelKnms,
  knmListDeleteKnms,
  knmListDisapproveKnms,
  knmListExportList,
  knmListExportRegistryKnm,
  knmListExportSimilaritiesList,
  knmListFindKnm,
  knmListGetData,
  knmListGetKnmSelectOptions,
  knmListRemoveFromPlanClick,
  knmListRemoveKnmsFromPlan,
  knmListRestoreKnms,
  knmListSetData,
  knmListSetFindData,
  knmListUpdateKnmsDuration,
  knmListUpdateKnmsStartDate,
} from '../actions/knmListActions';
import { KnmListActionsEnum } from '../enums/KnmListActionsEnum';
import { getKnmListData, getKnmListOrder, getKnmListPage } from '../selectors/knmListSelectors';
import { getRequestFilterData } from '../common/getRequestFilterData';
import { mapRawToData } from '../common/mapRawToData';
import { KnmVersionsEnum } from '../../../APIPackage/KnmModule/enums/KnmVersionsEnum';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';

const PAGE_SIZE = 50;

export function* watchKnmListSaga() {
  yield takeEvery(KnmListActionsEnum.KNM_LIST_INIT_PAGE, knmListInitPageSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_GET_DATA, knmListGetDataSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_GET_DATA_NEXT_PAGE, knmListGetDataNextPageSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_DELETE_KNMS, knmListDeleteKnmsSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_RESTORE_KNMS, knmListRestoreKnmsSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_CANCEL_KNMS, knmListCancelKnmsSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_DISAPPROVE_KNMS, knmListDisapproveKnmsSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_UPDATE_KNMS_START_DATE, knmListUpdateKnmsStartDateSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_UPDATE_KNMS_DURATION, knmListUpdateKnmsDurationSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_REMOVE_KNMS_FROM_PLAN, knmListRemoveKnmsFromPlanSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_EXPORT_LIST, knmListExportListSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_EXPORT_SIMILARITIES_LIST, knmListExportSimilaritiesListSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_EXPORT_REGISTRY_KNM, knmListExportRegistryKnmSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_FIND_KNM, knmListFindKnmSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_FIND_KNM_FOR_DECISION_REASON, knmListFindKnmForDecisionReasonSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_REMOVE_FROM_PLAN_CLICK, knmListRemoveFromPlanClickSaga);
  yield takeEvery(KnmListActionsEnum.KNM_LIST_GET_KNM_SELECT_OPTIONS, knmListGetKnmSelectOptionsSaga);
  yield takeEvery(KnmListActionsEnum.HAS_KNM_UNRESOLVED_VIOLATIONS, knmGetKnmUnresolvedViolations);
}

function* knmListInitPageSaga() {
  const planId = window.location.pathname.split('knms/')[1];
  const planNum = Number(planId);

  if (planNum) {
    yield put(knmListFilterSetPlanNum(planNum));
  } else {
    yield put(knmListFilterSetPlanNum());
  }
  yield put(knmListGetData());
}

function* knmListGetDataSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const order = getKnmListOrder(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const requestData: IKnmListFilterParams | null = getRequestFilterData(state);
    const res: IKnmIndexPage = yield fetchKnmFindIndex(0, PAGE_SIZE, requestData, order ?? '');

    yield put(knmListSetData(mapRawToData(res.list, isErknm), res.page, res.pageCount, res.totalCount, res.rejected));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmListFindKnmSaga(action: ActionType<typeof knmListFindKnm>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const { filterParam, size } = action.payload;
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
    let res: IKnmIndexPage | undefined;

    res = yield fetchKnmFindIndex(0, size || PAGE_SIZE, filterParam || null, '', !isErknm);
    if (!res?.list.length && isErknm) {
      res = yield fetchKnmFindIndex(0, size || PAGE_SIZE, filterParam || null, '', isErknm);
    }

    if (res) {
      yield put(knmListSetFindData(res.list));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmListFindKnmForDecisionReasonSaga(action: ActionType<typeof knmListFindKnm>) {
  function* funcTry() {
    const { filterParam, size } = action.payload;
    const res: IKnmIndexPage | undefined = yield fetchKnmFindIndex(
      0,
      size || PAGE_SIZE,
      filterParam || null,
      '',
      false,
    );

    if (res) {
      yield put(knmListSetFindData(res.list));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmListGetDataNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const data = getKnmListData(state);
    const page = getKnmListPage(state);
    const order = getKnmListOrder(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const requestData: IKnmListFilterParams | null = getRequestFilterData(state);

    const res: IKnmIndexPage = yield fetchKnmFindIndex(page + 1, PAGE_SIZE, requestData, order ?? '');

    yield put(
      knmListSetData(
        [...data, ...mapRawToData(res.list, isErknm)],
        res.page,
        res.pageCount,
        res.totalCount,
        res.rejected,
      ),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmListDeleteKnmsSaga(action: ActionType<typeof knmListDeleteKnms>) {
  yield sagaWrapper({
    *funcTry() {
      const { ids } = action.payload;

      yield knmDeleteSagaFunc(ids);
      yield put(knmListGetData());
    },
  });
}

function* knmListRestoreKnmsSaga(action: ActionType<typeof knmListRestoreKnms>) {
  yield sagaWrapper({
    *funcTry() {
      const { ids } = action.payload;

      yield ids.forEach(id => {
        fetchKnmRestoreById(id);
      });
      yield delay(REFRESH_DELAY);
      yield put(knmListGetData());
      yield put(
        addNoticeAction({
          renderContent: `КНМ успешно ${ids.length > 1 ? 'восстановлены' : 'восстановлен'}.`,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.DELETE_SUCCESS,
        }),
      );
    },
  });
}

function* knmListCancelKnmsSaga(action: ActionType<typeof knmListCancelKnms>) {
  yield sagaWrapper({
    *funcTry() {
      const { ids, commentText } = action.payload;

      yield fetchKnmsCancel({ knmIds: ids, note: commentText });
      yield delay(REFRESH_DELAY);
      yield put(knmListGetData());
    },
  });
}

function* knmListDisapproveKnmsSaga(action: ActionType<typeof knmListDisapproveKnms>) {
  yield sagaWrapper({
    *funcTry() {
      const { ids, rejectReasons } = action.payload;

      yield fetchKnmsDisapprove({ knmIds: ids, rejectReasons });
      yield delay(REFRESH_DELAY);
      yield put(knmListGetData());
    },
  });
}

function* knmListUpdateKnmsStartDateSaga(action: ActionType<typeof knmListUpdateKnmsStartDate>) {
  yield sagaWrapper({
    *funcTry() {
      const { ids, startDate } = action.payload;

      yield fetchKnmsUpdateStartDate({ knmIds: ids, startDate });
      yield delay(REFRESH_DELAY);
      yield put(knmListGetData());
    },
  });
}

function* knmListUpdateKnmsDurationSaga(action: ActionType<typeof knmListUpdateKnmsDuration>) {
  yield sagaWrapper({
    *funcTry() {
      const { ids, durationDays, durationHours } = action.payload;

      yield fetchKnmsUpdateDuration({ knmIds: ids, durationDays, durationHours });
      yield delay(REFRESH_DELAY);
      yield put(knmListGetData());
    },
  });
}

function* knmListRemoveKnmsFromPlanSaga(action: ActionType<typeof knmListRemoveKnmsFromPlan>) {
  function* funcTry() {
    const { ids, params } = action.payload;

    if (params) {
      if (!('label' in params)) {
        const { reason, document, signatureInfo, type } = params;

        yield fetchErknmRemoveFromPlan(ids, reason, {
          active: null,
          attachments: [
            {
              ...document,
              signatureInfo,
              signed: true,
            },
          ],
          correlationToken: null,
          description: null,
          guid: null,
          type,
        });
      } else if (params) {
        yield fetchKnmRemoveFromPlan({
          knmIds: ids.map(item => Number(item)),
          exclusionReason: deleteObjectProperties(params, ['label', 'value']),
        });
      }
    }
    yield delay(REFRESH_DELAY);
    yield put(
      addNoticeAction({
        renderContent: KNM_UNLIST_SUCCESSFULLY,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.UNLIST_KNM,
      }),
    );
    yield put(knmListGetData());
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: KNM_UNLIST_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.UNLIST_KNM,
        }),
      );
    },
  });
}

function* knmListExportListSaga(action: ActionType<typeof knmListExportList>) {
  function* funcTry() {
    const { notify, format, columns, filter } = action.payload;
    const knmFilter: IKnmListFilterParams | null = yield select(getRequestFilterData, filter);

    const res: IExport = yield exportKnmList(format, knmFilter, columns);

    if (res.id) {
      yield notify(res);
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmListExportSimilaritiesListSaga(action: ActionType<typeof knmListExportSimilaritiesList>) {
  function* funcTry() {
    const { notify, id, format, type } = action.payload;
    const res: IExport = yield exportSimilaritiesInKnmList(id, format, type);

    if (res.id) {
      yield notify(res);
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmListExportRegistryKnmSaga(action: ActionType<typeof knmListExportRegistryKnm>) {
  function* funcTry() {
    const { notify, id, type } = action.payload;
    const knm: IKnm | undefined = yield fetchKnm(id);
    const idRegistry = knm?.organization?.checkSubjectResult && knm.organization.checkSubjectResult[type]?.id;

    if (idRegistry && type.toUpperCase() in KnmRegistryTypeEnum) {
      const res: IExport = yield exportRegistryByType(
        String(idRegistry),
        type.toUpperCase() as keyof typeof KnmRegistryTypeEnum,
        ExportFormatsEnum.PDF,
      );

      if (res.id) {
        yield notify(res);
      }
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmListRemoveFromPlanClickSaga(action: ActionType<typeof knmListRemoveFromPlanClick>) {
  const { isAvailableKnms, filteredKnms, openRemoveFromPlanModal } = action.payload;
  const lawInterface: LawInterfacesEnum = yield select(getLawInterface);
  const isErknm = lawInterface === LawInterfacesEnum.FZ_248;
  const errNotice = addNoticeAction({
    noticeType: NoticeTypesEnum.ERROR,
    messageType: MessageTypesEnum.UNLIST_KNM,
    renderContent: KNM_NOT_AVAILABLE_UNLINK_FROM_PLAN,
  });

  if (isAvailableKnms) {
    const planIds = filteredKnms.map(el => Number(el.planId));
    const uniquePlanIds = planIds.reduce((acc: number[], planId: number) => {
      return acc.some(id => id === planId) ? acc : [...acc, planId];
    }, []);
    const plans: IKnmPlan[] = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const id of uniquePlanIds) {
      const knmPlan: IKnmPlan = yield fetchPlanById(id);

      plans.push(knmPlan);
    }
    const isAvailablePlans = plans?.every(plan =>
      isErknm
        ? getIsPlanErknmStatusApproved(getPlanErknmStatusByStatusTitle(plan.erknmStatus))
        : plan.status === getErpPlanLabel(PlanStatusEnum.APPROVED),
    );

    if (isAvailablePlans) {
      openRemoveFromPlanModal();
    } else {
      yield put(errNotice);
    }
  } else {
    yield put(errNotice);
  }
}

function* knmListGetKnmSelectOptionsSaga(action: ActionType<typeof knmListGetKnmSelectOptions>) {
  function* funcTry() {
    const { searchValue, isErp, callback } = action.payload;
    const filters: IKnmListFilterParams = {
      erpId: searchValue,
      pm: false,
      version: KnmVersionsEnum.ERKNM,
    };
    const result: IKnmIndexPage = yield fetchKnmFindIndex(0, 10, filters, undefined, isErp);
    const options: ISelectOption[] = result.list.map(item => ({
      id: item.erpId,
      label: item.erpId,
      value: item.erpId,
    }));

    callback(options);
  }

  yield sagaWrapper({ funcTry });
}

function* knmGetKnmUnresolvedViolations(action: ActionType<typeof hasKnmUnresolvedViolations>) {
  function* funcTry() {
    const { knmId, callback } = action.payload;
    const hasUnresolvedViolations: boolean = yield fetchGetKnmUnresolvedViolations(knmId);

    callback(hasUnresolvedViolations);
  }

  yield sagaWrapper({ funcTry });
}
